/* Inject dynamic brand CSS variables from BrandSettings active.
   Listens to BrandPreviewContext if provided (live preview from Studio). */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { readableOn } from "@/lib/color";

function hexToHsl(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hh = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hh = ((g - b) / d + (g < b ? 6 : 0)) * 60;
        break;
      case g:
        hh = ((b - r) / d + 2) * 60;
        break;
      default:
        hh = ((r - g) / d + 4) * 60;
    }
  }
  return `${Math.round(hh)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/* Ces variables sont posées en style inline sur <html>, ce qui l'emporte sur la
   règle `.dark` d'index.css : une teinte de charte reste donc la même dans les
   deux thèmes — c'est voulu, une marque ne change pas de couleur la nuit.

   La contrepartie, c'est que le thème ne peut plus corriger le texte posé
   dessus : `--secondary-foreground` vaut de l'encre sombre en thème sombre, et
   resterait illisible sur un fond de marque sombre. Chaque couleur injectée
   emporte donc SA couleur de texte, calculée par `readableOn`. */
function setPair(target, token, hex) {
  target.style.setProperty(token, hexToHsl(hex));
  target.style.setProperty(`${token}-foreground`, hexToHsl(readableOn(hex)));
}

export function applyBrand(brand, target = document.documentElement) {
  if (!brand) return;
  const { primaryColor, secondaryColor, accentColor, fontHeading, fontBody } = brand;
  if (primaryColor) {
    target.style.setProperty("--color-brand", primaryColor);
    target.style.setProperty("--ring", hexToHsl(primaryColor));
    setPair(target, "--primary", primaryColor);
  }
  if (secondaryColor) {
    target.style.setProperty("--color-secondary", secondaryColor);
    setPair(target, "--secondary", secondaryColor);
  }
  if (accentColor) {
    target.style.setProperty("--color-accent", accentColor);
    setPair(target, "--accent", accentColor);
  }
  if (fontHeading) target.style.setProperty("--font-heading", `"${fontHeading}", sans-serif`);
  if (fontBody) target.style.setProperty("--font-body", `"${fontBody}", sans-serif`);
}

export function loadBrandFonts(brand) {
  if (!brand) return;
  const fonts = [brand.fontHeading, brand.fontBody].filter(Boolean);
  fonts.forEach((f) => {
    const id = `gf-${f.replace(/\s+/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
      f
    )}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  });
}

export default function BrandStyleInjector({ override }) {
  const { data } = useQuery({
    queryKey: ["branding-active"],
    queryFn: async () => (await api.get("/branding/active")).data,
    staleTime: 5 * 60_000,
    enabled: !override,
  });

  useEffect(() => {
    const brand = override || data;
    if (!brand) return;
    applyBrand(brand);
    loadBrandFonts(brand);
  }, [data, override]);

  return null;
}
